import type { OAuthLoginData } from "@/betby/api/types";
import type { ApiResponse, RequestError } from "@/betby/api";
import LogUtil from "@/betby/utils/LogUtil";
import {
  TRACK_EVENT_SDK_START_INTERFACE_SPM,
  TRACK_EVENT_SDK_START_INTERFACE_TIME_SPM,
} from "@/betby/utils/TrackEvents";
import { LOGIN_BASIC_CONFIG } from "@/betby/utils/config";
import analyticsBridge from "@/betby/adapters/analytics/analyticsBridge";
import { authRepository } from "@/betby/models/auth/authRepository";
import {
  extractWalletBalances,
  isValidLoginResponse,
} from "@/betby/models/auth/authService";
import { resolveSessionTicket } from "@/betby/models/auth/authSessionResolver";
import {
  safeUpdateTrackingSDKWithLoginData,
  updateTrackingSDKFromSnapshot,
} from "@/betby/models/auth/authAnalyticsService";
import { registerHttpRuntime } from "@/betby/infrastructure/http/httpRuntime";
import { getAuthRuntime } from "@/betby/infrastructure/auth/authRuntime";
import { getGlobalUiRuntime } from "@/betby/infrastructure/ui/globalUiRuntime";
import { getHostBridge } from "@/betby/adapters/host/hostBridge";

function persistLoginSuccess(
  data: OAuthLoginData,
  responseTimestamp?: number,
): void {
  const runtime = getAuthRuntime();
  if (runtime?.persistLoginSuccess) {
    runtime.persistLoginSuccess(data, responseTimestamp);
    return;
  }

  LogUtil.warn("[auth] AuthRuntime 未注册，跳过 persistLoginSuccess");
}

function notifyAuthTokenExpired(message?: string, title?: string): void {
  LogUtil.info("[auth] Token expired, notifying host bridge and showing modal");

  // 1. 先通知宿主桥（触发 BetbyProvider 的 onTokenExpired 回调）
  const bridge = getHostBridge();
  if (bridge?.sendTokenExpired) {
    bridge.sendTokenExpired(message, title);
  } else {
    LogUtil.warn("[auth] HostBridge.sendTokenExpired 未注册");
  }

  // 2. 显示 token 过期弹窗
  const runtime = getGlobalUiRuntime();
  if (runtime?.showTokenExpiredModal) {
    runtime.showTokenExpiredModal(message, title);
    return;
  }

  LogUtil.warn("[auth] GlobalUiRuntime 未注册，无法展示 tokenExpired 弹窗");
}

function openAuthNetworkRetryModal(callbacks?: {
  onConfirm?: () => void;
  onClose?: () => void;
}): void {
  const runtime = getGlobalUiRuntime();
  if (runtime?.showNetworkRetryModal) {
    runtime.showNetworkRetryModal(callbacks);
    return;
  }

  LogUtil.warn("[auth] GlobalUiRuntime 未注册，无法展示 networkRetry 弹窗");
}

function confirmAuthTokenExpired(): void {
  const runtime = getGlobalUiRuntime();
  if (runtime?.confirmTokenExpired) {
    runtime.confirmTokenExpired();
    return;
  }

  LogUtil.warn("[auth] GlobalUiRuntime 未注册，无法发送 tokenExpired confirm");
}

let hasRegisteredHttpRuntime = false;

export function ensureAuthHttpRuntimeRegistered(): void {
  if (hasRegisteredHttpRuntime) {
    return;
  }

  // HTTP 层只读取 runtime 暴露的能力，避免直接依赖 store 或认证实现细节。
  registerHttpRuntime({
    getAuthSnapshot: () =>
      getAuthRuntime()?.getSnapshot?.() ?? {
        accessToken: null,
        refreshToken: null,
        expiresTime: null,
        userId: null,
        productId: null,
        businessId: null,
        userInfo: null,
        loginData: null,
        isAuthenticated: false,
      },
    getSessionTicket: () => resolveSessionTicket(),
    clearAuth: () => {
      getAuthRuntime()?.clearAuth?.();
    },
    applyLoginData: (data, responseTimestamp) => {
      persistLoginSuccess(data, responseTimestamp);
    },
    onTokenExpired: (message, title) => {
      notifyAuthTokenExpired(message, title);
    },
    onServerMaintenance: (message, title) => {
      notifyAuthTokenExpired(message, title);
    },
    onLogin: () => {
      getHostBridge()?.sendLogin();
    },
  });

  hasRegisteredHttpRuntime = true;
}

class AuthManager {
  private loginPromise: Promise<ApiResponse<OAuthLoginData>> | null = null;
  private isLoggingIn = false;

  async login(
    ticket: string,
    options?: { fromStartupPage?: boolean },
  ): Promise<ApiResponse<OAuthLoginData>> {
    if (this.isLoggingIn && this.loginPromise) {
      return this.loginPromise;
    }

    this.isLoggingIn = true;
    const requestOptions = options?.fromStartupPage
      ? { options: { showUnrecognizedErrorModal: true as const } }
      : undefined;

    this.loginPromise = authRepository
      .login(ticket, LOGIN_BASIC_CONFIG, requestOptions?.options)
      .then(async (response) => {
        const data = response?.data as OAuthLoginData | undefined;
        const isSuccess = response.code === 200 && isValidLoginResponse(data);

        safeUpdateTrackingSDKWithLoginData(data ?? null, isSuccess);

        if (!isSuccess) {
          LogUtil.error(
            "[AuthManager.login] 登录失败或响应缺少 token 信息",
            response,
          );

          const error: RequestError & { code?: number } = new Error(
            "Login request failed",
          );
          error.code = response.code;
          error.type = response.code === -1 ? "network" : "http";
          error.status = response.code;
          error.response = response;
          throw error;
        }

        LogUtil.success("登录成功:", response);

        if (data?.access_token && data?.refresh_token) {
          LogUtil.debug("[AuthManager.login] 准备保存 token...");
          persistLoginSuccess(data, response.timestamp);
        }

        return response;
      })
      .catch((error) => {
        LogUtil.error("登录失败:", error);

        const response = (
          error as RequestError & { response?: ApiResponse<OAuthLoginData> }
        )?.response;
        safeUpdateTrackingSDKWithLoginData(response?.data ?? null, false);
        getAuthRuntime()?.clearAuth?.();
        throw error;
      })
      .finally(() => {
        this.isLoggingIn = false;
        this.loginPromise = null;
      });

    return this.loginPromise;
  }

  reset(): void {
    this.isLoggingIn = false;
    this.loginPromise = null;
  }
}

export const authManager = new AuthManager();

async function fetchBalance(): Promise<void> {
  try {
    const snapshot = getAuthRuntime()?.getSnapshot?.();
    const accessToken = snapshot?.accessToken;
    const isAuthenticated = snapshot?.isAuthenticated;

    LogUtil.debug(
      "[fetchBalance] access_token:",
      accessToken ? "存在" : "不存在",
      "isAuthenticated:",
      isAuthenticated,
    );

    if (!accessToken || !isAuthenticated) {
      LogUtil.warn("[fetchBalance] 未登录，无法查询余额");
      return;
    }

    const balanceResponse = await authRepository.fetchBalance();
    LogUtil.info("[fetchBalance] balanceResponse:", balanceResponse);

    const balances = extractWalletBalances(balanceResponse?.data);
    if (balances) {
      getAuthRuntime()?.applyBalances?.(balances);
    }
  } catch (error) {
    LogUtil.error("[fetchBalance] 余额查询失败:", error);

    const err = error as RequestError;
    if (err?.type === "timeout" || err?.type === "network") {
      notifyAuthTokenExpired(
        "We're sorry, an error occurred. Please try re-entering the sports play later.",
      );
    }
  }
}

let initializeAuthPromise: Promise<void> | null = null;
let initializeAuthPromiseKey: string | null = null;

export async function initializeAuth(
  onSuccess?: (response: ApiResponse<OAuthLoginData>) => void,
  onError?: (error: RequestError & { code?: number }) => void,
  forceLogin: boolean = false,
): Promise<void> {
  const initialSnapshot = getAuthRuntime()?.getSnapshot?.();
  const accessToken = initialSnapshot?.accessToken;
  const isAuthenticated = initialSnapshot?.isAuthenticated;
  const dedupeKey = `${forceLogin ? "force" : "normal"}:${accessToken ? "token" : "no-token"}:${isAuthenticated ? "auth" : "no-auth"}:${resolveSessionTicket() ?? ""}`;

  // 启动期和页面切换都可能触发 initializeAuth；相同上下文直接复用同一条登录链路。
  if (initializeAuthPromise && initializeAuthPromiseKey === dedupeKey) {
    return initializeAuthPromise;
  }

  const run = async (): Promise<void> => {
    const snapshot = getAuthRuntime()?.getSnapshot?.();
    const currentAccessToken = snapshot?.accessToken;
    const currentIsAuthenticated = snapshot?.isAuthenticated;

    // 已经有有效会话时只补拉余额和埋点，不重复打登录接口。
    if (!forceLogin && currentAccessToken && currentIsAuthenticated) {
      LogUtil.info("已有有效的 access_token，跳过登录");
      updateTrackingSDKFromSnapshot(snapshot);
      await fetchBalance();
      return;
    }

    const ticket = resolveSessionTicket();

    if (!ticket) {
      LogUtil.warn("未找到 session_ticket，跳过登录");
      if (currentAccessToken && currentIsAuthenticated) {
        await fetchBalance();
      }
      return;
    }

    const requestStartTime = Date.now();
    let status: "suc" | "fail" = "fail";

    try {
      try {
        analyticsBridge.track(TRACK_EVENT_SDK_START_INTERFACE_SPM, {
          src: 516,
          eb_key1: "/login",
        });
      } catch (error) {
        LogUtil.error("[auth] Failed to track /login interface spm", error);
      }

      const response = await authManager.login(ticket, {
        fromStartupPage: true,
      });
      status = "suc";
      onSuccess?.(response);
    } catch (error) {
      onError?.(error as RequestError & { code?: number });
      const err = error as RequestError & { code?: number };
      const isNetworkError =
        err?.type === "timeout" || err?.type === "network" || err?.code === -1;

      if (isNetworkError) {
        // 登录链路网络失败时保留重试入口；关闭弹窗则走 tokenExpired confirm，和宿主保持一致退出语义。
        openAuthNetworkRetryModal({
          onConfirm: () => {
            authManager.reset();
            void initializeAuth(onSuccess, onError, forceLogin);
          },
          onClose: () => {
            confirmAuthTokenExpired();
          },
        });
      }
    } finally {
      try {
        const loadDurationMs = Math.max(Date.now() - requestStartTime, 0);
        analyticsBridge.track(TRACK_EVENT_SDK_START_INTERFACE_TIME_SPM, {
          src: 516,
          eb_key1: "/login",
          eb_key2: status,
          time: loadDurationMs,
        });
      } catch (error) {
        LogUtil.error("[auth] Failed to track /login interface time", error);
      }
    }
  };

  initializeAuthPromiseKey = dedupeKey;
  initializeAuthPromise = run();

  try {
    await initializeAuthPromise;
  } finally {
    initializeAuthPromise = null;
    initializeAuthPromiseKey = null;
  }
}
